import { Component, OnInit, EventEmitter, Output,
         OnDestroy, ViewChild, ElementRef }   from '@angular/core';
import { ActivatedRoute }                     from '@angular/router';
import { FormBuilder, FormGroup,
         FormControl, Validators, FormArray } from '@angular/forms';
import { Location }                           from '@angular/common';

import { ProgramSingle }                      from './../../../../models/program/program-single';
import { Admin }                              from './../../../../models/program/admin';
import { ProgramService }                     from './../../../../shared/services/program.service';
import { ProgramRoutesService }               from './../program-routes.service';
import { ErrorService }                       from './../../../../shared/services/error.service';
import { Subscription }                       from 'rxjs/Subscription';
import { ConnectableObservable }              from 'rxjs/observable/ConnectableObservable';
import { SearchService }                      from '../../../../shared/services/search.service';
import { Observable } from 'rxjs/Observable';
import { PermissionsService } from '../../../../shared/services/permissions.service';
import { SomePermissions } from '../../../../models/permissions';
import { EditionListService } from '../../../../editions/edition.service';

@Component({
  selector: 'app-program-settings',
  templateUrl: './program-settings.component.html',
  styleUrls: ['./program-settings.component.less']
})

export class ProgramSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('form') form: ElementRef;
  @Output() reset: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() openPreview: EventEmitter<void> = new EventEmitter<void>();
  public program: ProgramSingle;
  public titleMaxLength = 70;
  public analyticsAdmins: Admin[] = [];
  public errors: string[];
  public editor: any;
  public timeBound = false;
  public saving: boolean;
  public saved: boolean;
  public permissions: SomePermissions = {};

  private subs: Array<Subscription> = [];
  private parentRouteId: number;
  private tab = 'appearance';
  private showPreview = false;

  private fullListObs: ConnectableObservable<Admin[]>;

  // Form variables
  private enableUpload: boolean;
  public settings: FormGroup;
  public autoCohortSettings: FormGroup;

  constructor(
    public permissionsService: PermissionsService,
    private programService: ProgramService,
    private route: ActivatedRoute,
    private location: Location,
    private _programRoutes: ProgramRoutesService,
    private errorService: ErrorService,
    private _fb: FormBuilder,
    private editionListService: EditionListService,
    private search: SearchService
  ) { }

  ngOnInit(): void {
    this.fullListObs = this.search.queryAdmins('')
      .map((fullList) => fullList
        .map((admin => {return {name: admin.name, id: admin.id} as Admin}))
      ).publishReplay()
    this.subs.push(this.fullListObs.connect());

    this.subs.push(this.route.parent.params.subscribe(params => this.parentRouteId = +params['id']));
    this.subs.push(this.route.data.subscribe(data => this.tab = data.settings));

    this.permissionsService.checkPermission('nomadicAdministration').subscribe(permissions => {
      this.permissions.nomadicAdministration = permissions;
    });
    this.permissionsService.checkPermission('editionAdministration').subscribe(permissions => {
      this.permissions.editionAdministration = permissions;
    });

    this.programService
      .getProgram(this.parentRouteId)
      .subscribe(program => {
        this.program = JSON.parse(JSON.stringify(program));
        console.log(this.program);

        // Keep a reference to this section of the form, as we will need to refer
        // to it later to enable/disable based on the toggle state.
        this.autoCohortSettings = this._fb.group({
          token: [null],
          user_cap: [null],
          group_name_prefix: [null],
          time_bound: [false],
          duration: [null],
          max_wait: [null],
          start_notification_subject: [null],
          start_notification_message: [null],
          notify_countdown_day: [false],
          notify_countdown_week: [false],
          notify_individual_leaders: [false],
          notify_team_leaders: [false],
          notify_nudge: [false],
          nudge_delay: [null]
        });

        // Initialize settings edit form
        this.settings = this._fb.group({
          title: [null, [Validators.required, <any>Validators.maxLength(70)]],
          introduction: [program.introduction],
          image: [null],
          admins: this._fb.control([]),
          analytics_admins: this._fb.control([]),
          style: [null],
          style_secondary: [null],
          show_all_time_leaders: [null],
          hide_leaderboards: [null],
          price: [null, Validators.required],
          auto_cohort: this.autoCohortSettings,
          is_retired: [null],
          disable_sharing: [false],
        });

        this.setImageField(program);
        this.setInitialOptions();

        // This allows handling owner and admins as part of the same array, for a single UI component
        let mutatedProgram: any = Object.assign({}, program);
        if (mutatedProgram.owner) {
          mutatedProgram.admins = program.admins.slice();
          mutatedProgram.admins.unshift(program.owner);
          delete mutatedProgram.owner;
        };

        this.settings.patchValue(mutatedProgram);

        this.setAutoCohort(!!this.program.auto_cohort); // disable the controls if not present
      });
  }


  /**
   * Public Methods
   */

  public enableUploadToggle(toggle: boolean): void {
    this.enableUpload = toggle;
  }

  public introHandler(content: string): void {
    this.settings.controls['introduction'].markAsDirty();
    if (content.length < 1) {
      this.settings.controls['introduction'].patchValue(null);
    } else {
      this.settings.controls['introduction'].patchValue(content);
    }
  }

  public postImage(event: any): void {
    const ctrl: FormControl = (<any>this.settings).controls.image;
    ctrl.markAsDirty();
    ctrl.setValue(event.key);
  }

  public setAutoCohort(enabled: boolean): void {
    if (enabled) {
      this.autoCohortSettings.enable();
      this.setTimeBoundAccess(this.autoCohortSettings.controls['time_bound'].value);
    } else {
      this.autoCohortSettings.disable();
      this.setTimeBoundAccess(false);
    }
  }

  public setTimeBoundAccess(enabled: boolean): void {
    this.autoCohortSettings.controls['time_bound'].setValue(enabled);
    if (enabled) {
      this.autoCohortSettings.controls['duration'].enable();
      this.autoCohortSettings.controls['max_wait'].enable();
      this.autoCohortSettings.controls['notify_countdown_day'].enable();
      this.autoCohortSettings.controls['notify_countdown_week'].enable();
    } else {
      this.autoCohortSettings.controls['duration'].disable();
      this.autoCohortSettings.controls['max_wait'].disable();
      this.autoCohortSettings.controls['notify_countdown_day'].disable();
      this.autoCohortSettings.controls['notify_countdown_week'].disable();
    }
  }

  public setPrice(enabled: boolean): void {
    const price: FormControl = (<any>this.settings).controls.price;
    if (enabled) {
      price.setValue('');
    } else {
      price.setValue(0);
    }
  }

  public save(model: ProgramSingle, isValid: boolean): void {

    // This allows handling owner and admins as part of the same array, for a single UI component
    let mutatedModel = Object.assign({}, model);
    mutatedModel.admins = model.admins.slice();
    mutatedModel['owner'] = mutatedModel['admins'].shift();
    mutatedModel['admins'] = mutatedModel['admins'].filter(admin => admin.id !== mutatedModel['owner'].id)
    console.log(mutatedModel, isValid);

    let keysToBeSaved: string[];
    switch (this.tab) {
      case 'appearance':
        keysToBeSaved = ['title', 'introduction', 'style', 'style_secondary', 'image'];
        break;
      case 'admins':
        keysToBeSaved = ['analytics_admins', 'owner', 'admins'];
        break;
      case 'other-settings':
        keysToBeSaved = ['show_all_time_leaders', 'hide_leaderboards', 'price', 'auto_cohort', 'is_retired', 'disable_sharing'];
        break;
    }
    let patchMutatedModel = {}
    keysToBeSaved.map(key => patchMutatedModel[key] = model[key] === undefined ? null : model[key]);

    console.log(model, isValid);
    this.saving = true;
    this.saved = false;
    this.errors = [];
    if (model.introduction === '') model.introduction = null;
    this.programService.editProgram(this.parentRouteId, patchMutatedModel)
                       .subscribe(res => {
                         this.reset.emit(true);
                         this.saving = false;
                         this.saved = true;
                         if (this.showPreview) {
                           this.openPreview.emit();
                           this.showPreview = false;
                         }
                         if (this.settings.controls['title'].dirty) {
                          this.editionListService.refreshCurrentEdition();
                         }
                         this.settings.markAsPristine();
                         this.programService.getPrograms(true);
                       }, error => {
                         this.errors = this.errorService.messages(error);
                         this.saving = false;
                       });
  }

  public saveAndPreview(): void {
    this.showPreview = true;
    this.form.nativeElement.dispatchEvent(new Event('submit'));
  }

  public changeTab(event: Event, tabName: string) {
    this.tab = tabName;
    event.preventDefault();
  }

  public requestAutocompleteItems = (name: string): Observable<Admin[]> => {
    return this.fullListObs
  }

  /**
   * Private Methods
   */

  private setImageField(obj: any): void {
    let key: string = obj.image.split('/')[3];
    const coverImage: FormControl = (<any>this.settings).controls.image;
    coverImage.setValue(key);
  }

  private setInitialOptions(): void {
    const allTimeLeaders: FormControl = (<any>this.settings).controls.show_all_time_leaders;
    allTimeLeaders.setValue(this.program.show_all_time_leaders);
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/dashboard/program-list/program/program-settings/program-settings.component.ts